/**
 * VolumetricFogPass — analytic volumetric fog as a depth-aware post pass:
 * closed-form exponential height fog (Quilez), Henyey-Greenstein-style sun
 * in-scatter tinting, and up to MAX_FOG_VOLUMES analytic local box volumes
 * (uniform density × ray/AABB overlap — the UE "Local Fog Volume" fast
 * analytic integral).
 *
 * Replaces the old `render/gi/VolumetricFog.ts` stub with a REAL effect that
 * runs identically on WebGL2 and WebGPU (no compute, no froxel grid — the
 * light-injected froxel version is the documented v2 on top of this pass).
 *
 * Math (height fog, density d(y) = a·e^(−b·y), ray o + t·rd):
 *   opticalDepth = (a/b)·e^(−b·o_y)·(1 − e^(−b·rd_y·t))/rd_y     (rd_y ≠ 0)
 *                =  a·e^(−b·o_y)·t                                (rd_y → 0)
 *   transmittance = e^(−opticalDepth − Σ volumeOD)
 *   colour        = scene·T + fogColour·(1 − T)
 * with fogColour tinted toward the sun by pow(max(rd·sun, 0), sunPower).
 */

import type * as THREE from 'three/webgpu';
import { Vector3, Matrix4 } from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, exp, pow, mix, min, max,
  saturate, normalize, abs, select, perspectiveDepthToViewZ,
  cameraPosition, cameraWorldMatrix,
} from 'three/tsl';
import { skyMask } from '../depth/skyMask.js';

export const MAX_FOG_VOLUMES = 4;

export interface VolumetricFogPassConfig {
  /** Base fog colour (default 0.65, 0.72, 0.82). */
  color?: [number, number, number];
  /** Density at height 0 (default 0.015). */
  density?: number;
  /** Height falloff b — larger = thinner with altitude (default 0.06). */
  heightFalloff?: number;
  /** World height where density is measured from (default 0). */
  baseHeight?: number;
  /** Sun tint colour (default 1, 0.9, 0.7). */
  sunColor?: [number, number, number];
  /** Sun direction, world (default up-ish noon). */
  sunDirection?: [number, number, number];
  /** Sun forward-scatter exponent (default 8). */
  sunPower?: number;
  /** Start distance — fog-free near field (default 0). */
  startDistance?: number;
  /** Max fog opacity 0..1 (default 1). */
  maxOpacity?: number;
  enabled?: boolean;
  /**
   * EXPLICIT CAMERA (lighting agent 2026-09-25): read the camera position and
   * world matrix from `uniforms.cameraPos` / `uniforms.cameraWorld` (the caller
   * writes them every frame) instead of the ambient TSL camera nodes. WHY: this
   * node runs in the post chain's output QUAD, where `cameraPosition` /
   * `cameraWorldMatrix` bind to the quad's orthographic camera (the CloudRenderer
   * lesson) — the "world" ray came out in VIEW space (height fog measured along
   * screen-up) and a floor (`baseHeight`) could not be anchored. Default false
   * keeps the old behaviour for callers that never write the uniforms.
   */
  explicitCamera?: boolean;
}

export interface FogVolumeDef {
  center: [number, number, number];
  halfExtents: [number, number, number];
  density: number;
}

export interface VolumetricFogUniforms {
  color: { value: THREE.Vector3 };
  density: { value: number };
  heightFalloff: { value: number };
  baseHeight: { value: number };
  sunColor: { value: THREE.Vector3 };
  sunDirection: { value: THREE.Vector3 };
  sunPower: { value: number };
  startDistance: { value: number };
  maxOpacity: { value: number };
  /**
   * THE SKY'S SHARE (the night-sky realism wave, 2026-10-06): the fog's opacity over SKY pixels (the depth clear value,
   * render/depth/skyMask — builder-aware) as a fraction of its opacity over geometry. 1 (the default) = the old pass,
   * bit for bit: the sky's column of the layer veils it like the ground. 0 = the sky is left to the sky shader (which
   * draws its own airmass extinction and horizon brightening) — a starry sky survives a valley layer on a clear night.
   */
  skyOpacity: { value: number };
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  /** Per-volume uniforms (centers/halfExtents as Vector3, density scalar; density 0 = slot off). */
  volumeCenters: Array<{ value: THREE.Vector3 }>;
  volumeHalfExtents: Array<{ value: THREE.Vector3 }>;
  volumeDensities: Array<{ value: number }>;
  /** Convenience: write a slot (null clears). */
  setVolume(index: number, volume: FogVolumeDef | null): void;
  /** The scene camera (explicitCamera): render-space position + world matrix — write every frame. */
  cameraPos: { value: THREE.Vector3 };
  cameraWorld: { value: THREE.Matrix4 };
}

// ─── CPU mirrors (exported for tests) ───────────────────────────────────────

/** Closed-form exponential height-fog optical depth along a ray. */
export function heightFogOpticalDepthCpu(
  originY: number, rdY: number, dist: number,
  density: number, falloff: number, baseHeight: number,
): number {
  const oy = originY - baseHeight;
  const a = density;
  const b = falloff;
  if (Math.abs(rdY) < 1e-4) {
    return a * Math.exp(-b * oy) * dist;
  }
  // FOG_EXP_CAP (2026-09-27): a downward ray to the far plane overflows exp() in float32 — see the pass.
  return (a / b) * Math.exp(-b * oy) * (1 - Math.exp(Math.min(FOG_EXP_CAP, -b * rdY * dist))) / rdY;
}
/**
 * The largest exponent the slanted closed form may take (sky/post P2, 2026-09-27). A DOWNWARD ray to the far plane —
 * a sky pixel below the horizon (the ground not yet streamed, a cliff edge) — has −b·rdY·dist in the tens of
 * thousands: exp() overflows float32 to +Inf, (1 − Inf)/rdY = +Inf, and density × Inf is NaN at density 0 (a pinned
 * or tier-0 fog) — the NaN then spreads through the bloom pyramid and blacked whole frames (the noon A/B, 2026-09-27).
 * Capped, the optical depth is merely huge (trans 0 at any density > 0, exactly 0 at density 0).
 */
export const FOG_EXP_CAP = 80;

/**
 * CPU mirror of the composite (THE SKY'S SHARE, 2026-10-06): the fog over a pixel of colour `scene` with optical depth
 * `od` — opacity `maxOpacity`, × `skyOpacity` when the pixel is sky. skyOpacity 1 = the old pass exactly.
 */
export function fogCompositeCpu(
  scene: [number, number, number], fog: [number, number, number], od: number,
  maxOpacity: number, skyOpacity: number, isSky: boolean,
): [number, number, number] {
  const opacity = isSky ? maxOpacity * skyOpacity : maxOpacity;
  const t = 1 + (Math.exp(-od) - 1) * opacity; // mix(1, trans, opacity)
  return [fog[0] + (scene[0] - fog[0]) * t, fog[1] + (scene[1] - fog[1]) * t, fog[2] + (scene[2] - fog[2]) * t];
}

/** Ray/AABB overlap length clipped to [0, maxT]. */
export function rayBoxOverlapCpu(
  o: [number, number, number], rd: [number, number, number],
  center: [number, number, number], half: [number, number, number],
  maxT: number,
): number {
  let t0 = 0;
  let t1 = maxT;
  for (let i = 0; i < 3; i++) {
    const inv = 1 / (rd[i] || 1e-9);
    let tNear = (center[i] - half[i] - o[i]) * inv;
    let tFar = (center[i] + half[i] - o[i]) * inv;
    if (tNear > tFar) { const tmp = tNear; tNear = tFar; tFar = tmp; }
    t0 = Math.max(t0, tNear);
    t1 = Math.min(t1, tFar);
  }
  return Math.max(0, t1 - t0);
}

// ─── Pass factory ───────────────────────────────────────────────────────────

export function createVolumetricFogNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config?: VolumetricFogPassConfig,
): { node: unknown; uniforms: VolumetricFogUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const c = config?.color ?? [0.65, 0.72, 0.82];
  const sc = config?.sunColor ?? [1, 0.9, 0.7];
  const sd = config?.sunDirection ?? [0.3, 0.8, 0.5];

  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uDensity = uniform(config?.density ?? 0.015);
  const uFalloff = uniform(config?.heightFalloff ?? 0.06);
  const uBaseHeight = uniform(config?.baseHeight ?? 0);
  const uSunColor = uniform(vec3(sc[0], sc[1], sc[2]));
  const uSunDir = uniform(vec3(sd[0], sd[1], sd[2]));
  const uSunPower = uniform(config?.sunPower ?? 8);
  const uStart = uniform(config?.startDistance ?? 0);
  const uMaxOpacity = uniform(config?.maxOpacity ?? 1);
  const uSkyOpacity = uniform(1);
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);
  const uCamPos = uniform(new Vector3());
  const uCamWorld = uniform(new Matrix4());
  const camPosN = config?.explicitCamera ? uCamPos : cameraPosition;
  const camWorldN = config?.explicitCamera ? uCamWorld : cameraWorldMatrix;

  const volumeCenters: Array<ReturnType<typeof uniform>> = [];
  const volumeHalfExtents: Array<ReturnType<typeof uniform>> = [];
  const volumeDensities: Array<ReturnType<typeof uniform>> = [];
  for (let i = 0; i < MAX_FOG_VOLUMES; i++) {
    volumeCenters.push(uniform(vec3(0, 0, 0)));
    volumeHalfExtents.push(uniform(vec3(0, 0, 0)));
    volumeDensities.push(uniform(0));
  }

  const build = Fn(() => {
    const cIn = vec4(input as never);
    // THE LAZY-SAMPLE LAW (shader gaps program, 2026-09-29 — THE INERT FOG): three emits a texture node's sample ONCE,
    // as `var = texture(…)` where the node is FIRST built, and every later use of that node object reads the variable.
    // The `select(|rdY| < 1e-4, flat, slanted)` below compiles to an if/else on WebGL2, and the first build of the depth
    // sample landed INSIDE the `flat` branch — the `slanted` branch (every normal pixel) read an unassigned variable,
    // saw ~zero distance and drew no fog at all on WebGL2 HIGH (the generated GLSL: `nodeVar58 = texture(depth…)` inside
    // `if (abs(rd.y) < 0.0001)`). Whether it bit depended on whether an earlier pass in the same shader had already
    // sampled that same depth node at top level. Every depth-derived value is a VAR at the top of the function.
    const depthScalar = float(((depthNode as { r?: unknown }).r ?? depthNode) as never).toVar();
    const viewZ = perspectiveDepthToViewZ(depthScalar, uNear, uFar).toVar();
    // THE NIGHT BAND (sky/post 2026-09-27): the post quad's uv() is TOP-origin (three's getViewPosition flips it;
    // CloudRenderer's WHY), so NDC y is 1 − 2v. The old 2v − 1 MIRRORED the ray field: a screen-top sky pixel marched
    // DOWN to the far plane and saturated to the fog colour — black above twice the camera pitch at night, a flat pale
    // grey at noon, gone when the camera pitched up (p2-sky-2026-09-27/webgpu-high-nightband-*.png).
    const ndc = vec2(uv().x.mul(2).sub(1), float(1).sub(uv().y.mul(2)));
    const rayView = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(-1));
    const dist = viewZ.negate().mul(rayView.length()).toVar();
    // World ray via the camera's world matrix rotation (w=0 transform).
    const rdWorld = normalize(
      (camWorldN as typeof cameraWorldMatrix).mul(vec4(rayView, 0)).xyz,
    );
    const marched = max(dist.sub(uStart), float(0));

    // Height fog closed form, with the rdY→0 limit handled via select.
    const oy = (camPosN as typeof cameraPosition).y.sub(uBaseHeight);
    const rdY = rdWorld.y;
    const flat = uDensity.mul(exp(uFalloff.negate().mul(oy))).mul(marched);
    const slanted = uDensity.div(uFalloff)
      .mul(exp(uFalloff.negate().mul(oy)))
      .mul(float(1).sub(exp(min(uFalloff.negate().mul(rdY).mul(marched), float(FOG_EXP_CAP))))) // FOG_EXP_CAP: no Inf → no NaN at density 0
      .div(rdY);
    const heightOD = select(abs(rdY).lessThan(1e-4), flat, slanted);

    // Local box volumes: uniform density × ray overlap length.
    let volumeOD: ReturnType<typeof float> = float(0);
    for (let i = 0; i < MAX_FOG_VOLUMES; i++) {
      const bmin = (volumeCenters[i] as never as ReturnType<typeof vec3>).sub(volumeHalfExtents[i] as never);
      const bmax = (volumeCenters[i] as never as ReturnType<typeof vec3>).add(volumeHalfExtents[i] as never);
      const invD = vec3(1, 1, 1).div(rdWorld.add(vec3(1e-9, 1e-9, 1e-9)));
      const tA = bmin.sub(camPosN as typeof cameraPosition).mul(invD);
      const tB = bmax.sub(camPosN as typeof cameraPosition).mul(invD);
      const tMin = min(tA, tB);
      const tMax = max(tA, tB);
      const tEnter = max(max(tMin.x, tMin.y), max(tMin.z, float(0)));
      const tExit = min(min(tMax.x, tMax.y), min(tMax.z, dist));
      const overlap = max(tExit.sub(tEnter), float(0));
      volumeOD = volumeOD.add(overlap.mul(volumeDensities[i] as never)) as ReturnType<typeof float>;
    }

    const trans = exp(heightOD.add(volumeOD).negate());
    // THE SKY'S SHARE (2026-10-06): over a sky pixel the opacity is × skyOpacity (1 = the old pass exactly).
    const opacity = mix(uMaxOpacity, uMaxOpacity.mul(uSkyOpacity), skyMask(depthScalar));
    const transClamped = mix(float(1), trans, opacity);

    // Sun-tinted fog colour (forward-scatter glow toward the sun).
    const sunAmount = pow(saturate(rdWorld.dot(normalize(uSunDir))), uSunPower);
    const fogCol = mix(uColor, uSunColor, sunAmount);

    return vec4(mix(fogCol, cIn.rgb, transClamped), cIn.a);
  });

  const asVec3 = (u: ReturnType<typeof uniform>): { value: THREE.Vector3 } =>
    u as unknown as { value: THREE.Vector3 };
  const asNum = (u: ReturnType<typeof uniform>): { value: number } =>
    u as unknown as { value: number };

  const uniforms: VolumetricFogUniforms = {
    color: asVec3(uColor),
    density: asNum(uDensity),
    heightFalloff: asNum(uFalloff),
    baseHeight: asNum(uBaseHeight),
    sunColor: asVec3(uSunColor),
    sunDirection: asVec3(uSunDir),
    sunPower: asNum(uSunPower),
    startDistance: asNum(uStart),
    maxOpacity: asNum(uMaxOpacity),
    skyOpacity: asNum(uSkyOpacity),
    near: asNum(uNear),
    far: asNum(uFar),
    tanHalfFovY: asNum(uTanHalf),
    aspect: asNum(uAspect),
    volumeCenters: volumeCenters.map(asVec3),
    volumeHalfExtents: volumeHalfExtents.map(asVec3),
    volumeDensities: volumeDensities.map(asNum),
    cameraPos: uCamPos as unknown as { value: THREE.Vector3 },
    cameraWorld: uCamWorld as unknown as { value: THREE.Matrix4 },
    setVolume(index: number, volume: FogVolumeDef | null): void {
      if (index < 0 || index >= MAX_FOG_VOLUMES) return;
      if (!volume) {
        this.volumeDensities[index].value = 0;
        return;
      }
      this.volumeCenters[index].value.set(...volume.center);
      this.volumeHalfExtents[index].value.set(...volume.halfExtents);
      this.volumeDensities[index].value = volume.density;
    },
  };

  return { node: build(), uniforms };
}
